

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection, UiLabeledValue } from 'components/v2/record/DetailSection';

const ReferralRedemptionDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [redemption, setRedemption] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchRedemptionData();
    }, [id]);

    const fetchRedemptionData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/referral-redemptions/${id}`);
            setRedemption(response.data);
        } catch (error) {
            console.error('Error fetching referral redemption data:', error);
            setSnackbar({ open: true, message: 'Error loading referral redemption details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setRedemption(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/referral-redemptions/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Referral Redemption updated successfully', severity: 'success' });
            fetchRedemptionData();
        } catch (error) {
            console.error('Error updating referral redemption:', error);
            setSnackbar({ open: true, message: 'Error updating referral redemption', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this referral redemption?')) {
            try {
                await axiosInstance.delete(`/referral-redemptions/${id}`);
                setSnackbar({ open: true, message: 'Referral Redemption deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/referral-redemptions'), 1000);
            } catch (error) {
                console.error('Error deleting referral redemption:', error);
                setSnackbar({ open: true, message: 'Error deleting referral redemption', severity: 'error' });
            }
        }
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else {
            setSnackbar({
                open: true,
                message: `Unable to find user information for ${userName}.`,
                severity: 'info',
            });
        }
    };

    const capitalizeFirstLetter = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((item) => (
                    <Grid item xs={12} md={6} key={item.field}>
                        {['created_by_name', 'updated_by_name'].includes(item.field) ? (
                            <UiLabeledValue label={item.label}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    <strong>{item.label}:</strong>
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    onClick={() => {
                                        const userId = item.field === 'created_by_name' ? redemption.created_by : redemption.updated_by;
                                        handleUserClick(userId, redemption[item.field]);
                                    }}
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    sx={{
                                        cursor: 'pointer',
                                        ml: 1
                                    }}
                                >
                                    {capitalizeFirstLetter(redemption[item.field] || '')}
                                </MDTypography>
                            </MDBox>
                            </UiLabeledValue>
                        ) : (
                            <InlineEditField
                                label={item.label}
                                value={redemption?.[item.field]}
                                options={item.options || null}
                                onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                                isEditing={isEditing}
                                readOnly={item.readOnly || false}
                                isMultiSelect={item.isMultiSelect}
                                renderValue={item.renderValue || ((val) => (val ? String(val) : '-'))}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox py={3}>
                    <Skeleton variant="rectangular" height={400} />
                </MDBox>
                
            </DashboardLayout>
        );
    }

    if (!redemption) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox py={3}>
                    <MDTypography>Referral Redemption not found</MDTypography>
                </MDBox>
                
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">
                                        {redemption.name}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>
                                        Referral Redemption Details
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchRedemptionData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Redemption">
                                            <IconButton
                                                onClick={() => setIsEditing(true)}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete Redemption">
                                        <IconButton
                                            onClick={handleDelete}
                                            sx={commonIconButtonStyles}
                                        >
                                            <Delete sx={commonIconStyles} />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Basic Information", [
                                    { label: "Name", field: "name" },
                                    { label: "Tier Code", field: "tier_code" },
                                    { label: "Tier Rank", field: "tier_rank" },
                                    { label: "Status", field: "status", options: [{ value: "Draft", label: "Draft" }, { value: "Active", label: "Active" }, { value: "Retired", label: "Retired" }] },
                                    { label: "Redemption Conversion", field: "redemption_conversion", renderValue: (val) => val ? parseFloat(val).toFixed(4) : '-' },
                                    { label: "Override Rule", field: "override_rule", options: [{ value: true, label: "Yes" }, { value: false, label: "No" }] },
                                    { label: "Earn Multiplier", field: "earn_multiplier", renderValue: (val) => val ? parseFloat(val).toFixed(2) : '-' },
                                    { label: "Qualify Metric", field: "qualify_metric", options: [{ value: "Points", label: "Points" }, { value: "Trips", label: "Trips" }, { value: "Revenue", label: "Revenue" }] },
                                    { label: "Qualify Threshold", field: "qualify_threshold", renderValue: (val) => val ? parseFloat(val).toFixed(2) : '-' },
                                    { label: "Validity Months", field: "validity_months" },
                                    { label: "Benefits", field: "benefits" },
                                    { label: "Notes", field: "notes" },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name" },
                                    { label: "Modified By", field: "updated_by_name" },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default ReferralRedemptionDetail;

